import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import InformationBox from '@repo/ui/information-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'

import { getAffectedStaffQuestionnaire } from '../../../api/affected-staffs/get-affected-staff-questionnaire'
import { saveAffectedStaffQuestionnaire } from '../../../api/affected-staffs/save-affected-staff-questionnaire'
import { submitAffectedStaffQuestionnaire } from '../../../api/affected-staffs/submit-affected-staff-questionnaire'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import StaffPersonalSpaceQuestionnaireForm from '../../../components/pages/staff-personal-space/my-journeys/questionnaire/staff-personal-space-questionnaire-form'
import StaffPersonalSpaceQuestionnaireView from '../../../components/pages/staff-personal-space/my-journeys/questionnaire/view/staff-personal-space-questionnaire-view'
import { Paths } from '../../../lib/constants/path'
import useAffectedStaff from '../../../lib/hooks/use-affected-staff'
import useTranslate from '../../../lib/hooks/use-translate'
import { canAffectedStaffAccessQuestionnaire } from '../../../lib/utils/functions/affected-staff-questionnaire.functions'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import {
  getAffectedStaffQuestionnaireBasedOnHostFamily,
  mapAffectedStaffQuestionnaireToInput
} from './utils'

export default function StaffPersonalSpaceQuestionnaire() {
  const { t } = useTranslate()
  const { affectedStaff } = useAffectedStaff()
  const queryClient = useQueryClient()
  const canViewQuestionnaire = canAffectedStaffAccessQuestionnaire(affectedStaff)

  const submitAffectedStaffQuestionnaireMutation = useMutation({
    mutationFn: submitAffectedStaffQuestionnaire,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('questionnaire.toast.success'))
      if (affectedStaff) {
        queryClient.invalidateQueries({
          queryKey: ['affected-staff-questionnaire', affectedStaff.id]
        })
      }
    }
  })

  const saveAffectedStaffQuestionnaireMutation = useMutation({
    mutationFn: saveAffectedStaffQuestionnaire,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('questionnaire.toast.success'))
      if (affectedStaff) {
        queryClient.invalidateQueries({
          queryKey: ['affected-staff-questionnaire', affectedStaff.id]
        })
      }
    }
  })

  const { data, isPending } = useQuery({
    queryKey: ['affected-staff-questionnaire', affectedStaff?.id],
    queryFn: () => getAffectedStaffQuestionnaire(affectedStaff?.id ?? 0),
    enabled: !!affectedStaff && canViewQuestionnaire
  })

  const hasQuestionnaire = !!data

  if (!affectedStaff) return null
  if (!canViewQuestionnaire) {
    return <Navigate replace to={`../${Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_HOME}`} />
  }
  if (isPending) return <LoadingScreen />

  return (
    <div>
      <Typography variant="h3">
        {t('staff.personal.space.my.journeys.details.questionnaire')}
      </Typography>
      <div className="h-4" />
      <InformationBox isHiddenOnMobile={false}>
        <div className="flex flex-col gap-2 pr-3">
          <Typography variant="secondary-body" color={tailwindColors.black.DEFAULT}>
            {t('affected.staff.questionnaire.information.1')}
            <strong> {t('affected.staff.questionnaire.information.2')} </strong>
            {t('affected.staff.questionnaire.information.3')}
          </Typography>
        </div>
      </InformationBox>
      <div className="h-4" />
      {hasQuestionnaire && data.isSubmitted && data ? (
        <StaffPersonalSpaceQuestionnaireView
          affectedStaffName={toFullName(
            affectedStaff.staff.firstName,
            affectedStaff.staff.lastName
          )}
          questionnaire={data}
        />
      ) : (
        <StaffPersonalSpaceQuestionnaireForm
          defaultValues={data ? mapAffectedStaffQuestionnaireToInput(data) : undefined}
          handleSubmit={async (data) => {
            await submitAffectedStaffQuestionnaireMutation.mutateAsync({
              affectedStaffId: affectedStaff.id,
              input: getAffectedStaffQuestionnaireBasedOnHostFamily(data, canViewQuestionnaire)
            })
          }}
          handleSave={async (data) => {
            await saveAffectedStaffQuestionnaireMutation.mutateAsync({
              affectedStaffId: affectedStaff.id,
              input: getAffectedStaffQuestionnaireBasedOnHostFamily(data, canViewQuestionnaire)
            })
          }}
        />
      )}
    </div>
  )
}
